import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  Typography,
  Chip,
  Stack,
  Button,
  CircularProgress,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  alpha,
} from "@mui/material";
import {
  CheckCircle as BilledIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  PhoneAndroid as AndroidIcon,
  Language as WeberpIcon,
  Summarize as TotalIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Place as StateIcon,
  ArrowBack as BackIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import PageContentLayout from "../components/layout/PageContentLayout";
import apiService from "../services/apiService";
import { crmColors } from "../theme/colors";

const shellSx = {
  borderRadius: 2.5,
  border: `1px solid ${crmColors.border.default}`,
  boxShadow: "0 6px 18px -14px rgba(15, 31, 47, 0.22)",
  overflow: "hidden",
  background: crmColors.background.paper,
};

//Created By Cursor — full India states / UTs for billed state cards
const ALL_STATES = [
  "Andaman and Nicobar Islands",
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chandigarh",
  "Chhattisgarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jammu and Kashmir",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Ladakh",
  "Lakshadweep",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Puducherry",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
];

const normalizeStateKey = (name) =>
  String(name || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString();
};

const fyIdOf = (fy) =>
  fy?.financial_year_id || fy?.year_id || fy?.id || fy?.year || "";
const fyLabelOf = (fy) =>
  fy?.financial_year_name || fy?.name || fy?.label || String(fyIdOf(fy) || "");

//Created By Cursor — Billed / Grace status chip
const billingChip = (row) => {
  const inGrace =
    Boolean(row.in_grace) ||
    String(row.display_status || row.billing_status || "").toLowerCase() === "grace";
  let label = "Billed";
  if (inGrace) {
    const left =
      row.grace_days_left != null && !Number.isNaN(Number(row.grace_days_left))
        ? Math.max(0, Number(row.grace_days_left))
        : null;
    if (left == null) label = "Grace";
    else if (left <= 0) label = "Grace · Last day";
    else label = `Grace · ${left} day${left === 1 ? "" : "s"} left`;
  }
  return (
    <Chip
      size="small"
      label={label}
      sx={{
        fontWeight: 800,
        height: 24,
        borderRadius: "8px",
        color: "#fff",
        bgcolor: inGrace ? crmColors.error.main : crmColors.success.main,
      }}
    />
  );
};

const productChip = (row) => {
  const line = String(row.product_line || "").toLowerCase();
  const isWeberp = line === "weberp" || Number(row.has_weberp) === 1;
  return (
    <Chip
      size="small"
      icon={isWeberp ? <WeberpIcon sx={{ fontSize: "14px !important" }} /> : <AndroidIcon sx={{ fontSize: "14px !important" }} />}
      label={isWeberp ? "WebERP" : "Android"}
      sx={{
        fontWeight: 800,
        height: 24,
        borderRadius: "8px",
        bgcolor: isWeberp
          ? alpha(crmColors.info.main, 0.12)
          : alpha(crmColors.success.main, 0.12),
        color: isWeberp ? crmColors.info.dark : crmColors.success.dark,
        border: `1px solid ${
          isWeberp ? alpha(crmColors.info.main, 0.28) : alpha(crmColors.success.main, 0.28)
        }`,
        "& .MuiChip-icon": {
          color: isWeberp ? crmColors.info.main : crmColors.success.main,
        },
      }}
    />
  );
};

const KpiTile = ({ label, value, color, icon, active, onClick }) => (
  <Box
    component={motion.button}
    type="button"
    whileTap={{ scale: 0.98 }}
    onClick={onClick}
    sx={{
      p: 1.75,
      width: "100%",
      textAlign: "left",
      cursor: onClick ? "pointer" : "default",
      borderRadius: "14px",
      border: `1.5px solid ${active ? color : alpha(color, 0.28)}`,
      background: `linear-gradient(145deg, ${alpha(color, active ? 0.16 : 0.1)} 0%, #fff 70%)`,
      display: "flex",
      alignItems: "center",
      gap: 1.35,
      boxShadow: active ? `0 8px 18px -12px ${alpha(color, 0.55)}` : "none",
    }}
  >
    <Avatar
      sx={{
        width: 42,
        height: 42,
        bgcolor: alpha(color, 0.14),
        color,
        border: `1px solid ${alpha(color, 0.22)}`,
      }}
    >
      {icon}
    </Avatar>
    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 800,
          letterSpacing: "0.06em",
          color: crmColors.text.secondary,
          textTransform: "uppercase",
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontWeight: 800, fontSize: "1.45rem", color: crmColors.text.primary, lineHeight: 1.15 }}>
        {value}
      </Typography>
    </Box>
  </Box>
);

const BilledSchools = () => {
  const navigate = useNavigate();
  const [financialYears, setFinancialYears] = useState([]);
  const [financialYearId, setFinancialYearId] = useState("");
  const [fyAnchor, setFyAnchor] = useState(null);

  const [rows, setRows] = useState([]);
  const [states, setStates] = useState([]);
  const [kpis, setKpis] = useState({ android: 0, weberp: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [productFilter, setProductFilter] = useState("all"); // all | android | weberp

  //Created By Cursor — load financial years
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = await apiService.getFinancialYearList();
      if (cancelled) return;
      if (result.success && Array.isArray(result.data)) {
        setFinancialYears(result.data);
        const current = result.data.find((fy) => {
          const status = String(
            fy.status || fy.financial_year_status || fy.current_status || ""
          ).toLowerCase();
          return status === "current";
        });
        const first = current || result.data[0];
        if (first) setFinancialYearId(String(fyIdOf(first)));
      } else {
        toast.error(result.error || "Failed to load financial years");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedFy = useMemo(
    () => financialYears.find((fy) => String(fyIdOf(fy)) === String(financialYearId)),
    [financialYears, financialYearId]
  );

  const load = useCallback(async () => {
    if (!financialYearId) return;
    setLoading(true);
    const result = await apiService.getBilledSchools({
      limit: 2000,
      offset: 0,
      financial_year_id: financialYearId,
      state: "",
    });
    setLoading(false);
    if (result.success) {
      setRows(Array.isArray(result.data) ? result.data : []);
      setStates(Array.isArray(result.states) ? result.states : []);
      setKpis(result.kpis || { android: 0, weberp: 0, total: 0 });
    } else {
      toast.error(result.error || "Failed to load billed schools");
      setRows([]);
      setStates([]);
      setKpis({ android: 0, weberp: 0, total: 0 });
    }
  }, [financialYearId]);

  useEffect(() => {
    load();
  }, [load]);

  const filteredRows = useMemo(() => {
    let list = rows;
    if (selectedState) {
      const want = normalizeStateKey(selectedState);
      list = list.filter((r) => {
        const st = String(r.state || "").trim() || "Unknown";
        return normalizeStateKey(st) === want;
      });
    }
    if (productFilter === "android") {
      list = list.filter((r) => Number(r.has_weberp) !== 1 && Number(r.has_android) === 1);
    } else if (productFilter === "weberp") {
      list = list.filter((r) => Number(r.has_weberp) === 1);
    }
    const term = searchTerm.trim().toLowerCase();
    if (term) {
      list = list.filter((r) =>
        [r.school_name, r.school_code, r.admin_name, r.admin_phone, r.state, r.country, r.product_line]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(term)
      );
    }
    return list;
  }, [rows, selectedState, productFilter, searchTerm]);

  //Created By Cursor — All States card + every India state (merge API counts)
  const stateCards = useMemo(() => {
    const byKey = {};
    (states || []).forEach((st) => {
      const key = normalizeStateKey(st.state);
      if (!key) return;
      byKey[key] = {
        state: st.state,
        count: Number(st.count) || 0,
        android: Number(st.android) || 0,
        weberp: Number(st.weberp) || 0,
      };
    });

    const cards = ALL_STATES.map((name) => {
      const hit = byKey[normalizeStateKey(name)];
      return {
        state: name,
        count: hit?.count || 0,
        android: hit?.android || 0,
        weberp: hit?.weberp || 0,
        isAll: false,
      };
    });

    // Include any API states not in the master list (e.g. Unknown / typos)
    Object.values(byKey).forEach((st) => {
      const known = ALL_STATES.some(
        (n) => normalizeStateKey(n) === normalizeStateKey(st.state)
      );
      if (!known) {
        cards.push({ ...st, isAll: false });
      }
    });

    cards.sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return String(a.state).localeCompare(String(b.state));
    });

    return [
      {
        state: "All States",
        count: kpis.total || rows.length,
        android: kpis.android || 0,
        weberp: kpis.weberp || 0,
        isAll: true,
      },
      ...cards,
    ];
  }, [states, kpis, rows.length]);

  return (
    <PageContentLayout
      title="Billed Schools"
      subtitle="Paid schools by product line and state"
      darkBar
      barSubtitle={`${kpis.total || 0} billed · ${selectedFy ? fyLabelOf(selectedFy) : "…"}`}
      breadcrumbs={[
        { label: "Billed Schools", icon: <BilledIcon sx={{ color: "#ffffff", fontSize: 18 }} /> },
      ]}
      backTo="/dashboard"
      actions={
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            size="small"
            variant="contained"
            disableElevation
            endIcon={<ArrowDownIcon />}
            onClick={(e) => setFyAnchor(e.currentTarget)}
            sx={{
              textTransform: "none",
              fontWeight: 800,
              borderRadius: "10px",
              height: 36,
              bgcolor: alpha("#fff", 0.16),
              color: "#fff",
              border: `1px solid ${alpha("#fff", 0.35)}`,
              "&:hover": { bgcolor: alpha("#fff", 0.24) },
            }}
          >
            {selectedFy ? fyLabelOf(selectedFy) : "Financial Year"}
          </Button>
          <Menu
            anchorEl={fyAnchor}
            open={Boolean(fyAnchor)}
            onClose={() => setFyAnchor(null)}
            PaperProps={{
              sx: {
                mt: 0.75,
                borderRadius: 2,
                border: `1px solid ${crmColors.border.default}`,
                maxHeight: 320,
              },
            }}
          >
            {financialYears.map((fy) => {
              const id = String(fyIdOf(fy));
              return (
                <MenuItem
                  key={id}
                  selected={id === String(financialYearId)}
                  onClick={() => {
                    setFinancialYearId(id);
                    setSelectedState("");
                    setFyAnchor(null);
                  }}
                  sx={{ fontWeight: id === String(financialYearId) ? 800 : 600, fontSize: 13 }}
                >
                  {fyLabelOf(fy)}
                </MenuItem>
              );
            })}
          </Menu>
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={load}
            disabled={loading}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              height: 36,
              borderRadius: "10px",
              color: "#fff",
              borderColor: alpha("#fff", 0.45),
              "&:hover": { borderColor: "#fff", bgcolor: alpha("#fff", 0.08) },
            }}
          >
            Refresh
          </Button>
        </Stack>
      }
    >
      <Box
        component={motion.div}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.28 }}
        sx={{ display: "flex", flexDirection: "column", gap: 1.75 }}
      >
        {/* KPIs */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
            gap: 1.25,
          }}
        >
          <KpiTile
            label="Android"
            value={kpis.android || 0}
            color={crmColors.success.main}
            icon={<AndroidIcon />}
            active={productFilter === "android"}
            onClick={() =>
              setProductFilter((p) => (p === "android" ? "all" : "android"))
            }
          />
          <KpiTile
            label="WebERP"
            value={kpis.weberp || 0}
            color={crmColors.info.main}
            icon={<WeberpIcon />}
            active={productFilter === "weberp"}
            onClick={() =>
              setProductFilter((p) => (p === "weberp" ? "all" : "weberp"))
            }
          />
          <KpiTile
            label="Total"
            value={kpis.total || 0}
            color={crmColors.primary.main}
            icon={<TotalIcon />}
            active={productFilter === "all"}
            onClick={() => setProductFilter("all")}
          />
        </Box>

        {/* State cards */}
        <Card elevation={0} sx={{ ...shellSx, p: { xs: 1.5, md: 2 } }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            sx={{ mb: 1.5 }}
            spacing={1}
          >
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: 15 }}>States</Typography>
              <Typography sx={{ fontSize: 12, color: crmColors.text.secondary, fontWeight: 600 }}>
                Tap a state to see its billed school list
              </Typography>
            </Box>
            {selectedState ? (
              <Button
                size="small"
                startIcon={<BackIcon />}
                onClick={() => setSelectedState("")}
                sx={{ textTransform: "none", fontWeight: 800 }}
              >
                All states
              </Button>
            ) : null}
          </Stack>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(2, 1fr)",
                  sm: "repeat(3, 1fr)",
                  md: "repeat(4, 1fr)",
                  lg: "repeat(6, 1fr)",
                },
                gap: 1,
              }}
            >
              {stateCards.map((st) => {
                const isAll = Boolean(st.isAll);
                const key = isAll ? "__all__" : st.state;
                const selected = isAll
                  ? !selectedState
                  : normalizeStateKey(selectedState) === normalizeStateKey(st.state);
                const hasCount = Number(st.count) > 0;
                return (
                  <Box
                    key={key}
                    component={motion.button}
                    type="button"
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setSelectedState(isAll ? "" : st.state)}
                    sx={{
                      p: 1.35,
                      borderRadius: "12px",
                      textAlign: "left",
                      cursor: "pointer",
                      gridColumn: isAll ? { xs: "span 2", sm: "span 1", md: "span 2" } : "span 1",
                      border: `1.5px solid ${
                        selected
                          ? crmColors.primary.main
                          : isAll
                            ? alpha(crmColors.primary.main, 0.35)
                            : crmColors.border.default
                      }`,
                      bgcolor: selected
                        ? alpha(crmColors.primary.main, 0.12)
                        : isAll
                          ? alpha(crmColors.primary.main, 0.06)
                          : hasCount
                            ? "#fff"
                            : alpha(crmColors.text.secondary, 0.03),
                      boxShadow: selected
                        ? `0 6px 14px -10px ${alpha(crmColors.primary.main, 0.5)}`
                        : "none",
                      opacity: hasCount || isAll || selected ? 1 : 0.72,
                      transition: "border-color 0.15s ease, background 0.15s ease",
                      "&:hover": {
                        borderColor: crmColors.primary.main,
                        bgcolor: alpha(crmColors.primary.main, 0.08),
                        opacity: 1,
                      },
                    }}
                  >
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
                      <StateIcon
                        sx={{
                          fontSize: 16,
                          color: selected
                            ? crmColors.primary.main
                            : isAll
                              ? crmColors.primary.main
                              : crmColors.text.secondary,
                        }}
                      />
                      <Typography
                        sx={{
                          fontWeight: 800,
                          fontSize: isAll ? 14 : 12.5,
                          lineHeight: 1.2,
                          color: crmColors.text.primary,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={st.state}
                      >
                        {st.state}
                      </Typography>
                    </Stack>
                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: isAll ? "1.45rem" : "1.2rem",
                        lineHeight: 1.1,
                        color: hasCount || isAll ? crmColors.text.primary : crmColors.text.disabled,
                      }}
                    >
                      {st.count}
                    </Typography>
                    <Typography
                      sx={{
                        mt: 0.5,
                        fontSize: 11,
                        fontWeight: 700,
                        color: crmColors.text.secondary,
                      }}
                    >
                      Android {st.android || 0} · WebERP {st.weberp || 0}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          )}
        </Card>

        {/* Details list */}
        <Card elevation={0} sx={shellSx}>
          <Box
            sx={{
              px: { xs: 1.75, md: 2.25 },
              py: 1.5,
              borderBottom: `1px solid ${crmColors.border.default}`,
              display: "flex",
              flexWrap: "wrap",
              gap: 1.25,
              alignItems: "center",
              justifyContent: "space-between",
              background: `linear-gradient(90deg, ${alpha(crmColors.primary.main, 0.05)} 0%, transparent 70%)`,
            }}
          >
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: 14 }}>
                {selectedState ? `${selectedState} · schools` : "All billed schools"}
              </Typography>
              <Typography sx={{ fontSize: 12, color: crmColors.text.secondary, fontWeight: 600 }}>
                {filteredRows.length} shown
                {productFilter !== "all"
                  ? ` · ${productFilter === "android" ? "Android" : "WebERP"} filter`
                  : ""}
              </Typography>
            </Box>
            <TextField
              size="small"
              placeholder="Search school, code, contact…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: crmColors.primary.main, fontSize: 18 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                minWidth: { xs: "100%", sm: 260 },
                "& .MuiOutlinedInput-root": {
                  height: 38,
                  borderRadius: "10px",
                  bgcolor: "#fff",
                },
              }}
            />
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 7 }}>
              <CircularProgress size={32} />
            </Box>
          ) : filteredRows.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 7, px: 2 }}>
              <Typography sx={{ fontWeight: 800, mb: 0.5 }}>No billed schools</Typography>
              <Typography variant="body2" color="text.secondary">
                Try another financial year, state, or product filter.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow
                    sx={{
                      bgcolor: alpha(crmColors.primary.main, 0.035),
                      "& th": {
                        fontWeight: 800,
                        fontSize: 11,
                        letterSpacing: "0.04em",
                        textTransform: "uppercase",
                        color: crmColors.text.secondary,
                        borderBottom: `1px solid ${crmColors.border.default}`,
                        py: 1.2,
                      },
                    }}
                  >
                    <TableCell>School</TableCell>
                    <TableCell>Product</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>State</TableCell>
                    <TableCell>Valid Until</TableCell>
                    <TableCell align="right">Order</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRows.map((row) => (
                    <TableRow
                      key={row.school_code}
                      hover
                      sx={{
                        cursor: "pointer",
                        "& td": {
                          borderBottom: `1px solid ${crmColors.border.default}`,
                          py: 1.25,
                        },
                        "&:hover": { bgcolor: alpha(crmColors.primary.main, 0.03) },
                      }}
                      onClick={() =>
                        navigate(`/schools/detail/${encodeURIComponent(row.school_code)}`)
                      }
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 800 }}>
                          {row.school_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          {row.school_code}
                          {row.admin_name ? ` · ${row.admin_name}` : ""}
                        </Typography>
                      </TableCell>
                      <TableCell>{productChip(row)}</TableCell>
                      <TableCell>{billingChip(row)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {row.state || "Unknown"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.country || ""}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {formatDate(row.service_expiry)}
                        </Typography>
                        {row.in_grace && row.grace_end_date ? (
                          <Typography
                            variant="caption"
                            sx={{ color: crmColors.error.main, fontWeight: 700 }}
                          >
                            Grace ends {formatDate(row.grace_end_date)}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell align="right">
                        <Typography sx={{ fontWeight: 800, fontSize: 13 }}>
                          {row.total_amount != null
                            ? `₹${Number(row.total_amount).toLocaleString()}`
                            : "—"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>
      </Box>
    </PageContentLayout>
  );
};

export default BilledSchools;
